"use client";

import axios from "axios";

// Client-side API that goes through proxy to backend
export const apiClient = axios.create({
  baseURL: "/api", // Proxy will handle routing
  headers: {
    "Content-Type": "application/json",
  },
});

// Only add interceptors on client-side (interceptors run at module load time)
if (typeof window !== "undefined") {
  // Add request interceptor to include auth token
  apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem("auth-token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Add response interceptor to handle token refresh on 401 errors
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // If we get a 401 and haven't already tried to refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const refreshToken = localStorage.getItem("refresh-token");
          if (!refreshToken) {
            // No refresh token available, redirect to login
            window.location.href = "/auth/login";
            return Promise.reject(error);
          }

          // Attempt to refresh the token using new v2 endpoint
          const refreshResponse = await fetch("/api/v1/auth/refresh", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ refreshToken }),
          });

          if (refreshResponse.ok) {
            const tokenData = await refreshResponse.json();

            // Update localStorage with new tokens
            localStorage.setItem("auth-token", tokenData.token);
            localStorage.setItem("refresh-token", tokenData.refreshToken);

            // Update the original request with new token
            originalRequest.headers.Authorization = `Bearer ${tokenData.token}`;

            // Trigger auth store update if available
            try {
              const { useAuthStore } = await import("@/stores/auth-store");
              useAuthStore.getState().updateTokens(tokenData);
            } catch (storeError) {
              console.warn("Failed to update auth store:", storeError);
            }

            // Retry the original request
            return apiClient(originalRequest);
          } else {
            // Refresh failed, redirect to login
            localStorage.removeItem("auth-token");
            localStorage.removeItem("refresh-token");
            window.location.href = "/auth/login";
          }
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem("auth-token");
          localStorage.removeItem("refresh-token");
          window.location.href = "/auth/login";
        }
      }

      return Promise.reject(error);
    }
  );
}
